import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { trpcClient, type EmailWaveSummary } from '@/lib/trpc'
import { Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

export function EmailWaveReport() {
  const [waves, setWaves] = useState<EmailWaveSummary[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [failures, setFailures] = useState<Record<string, string[]>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await trpcClient.batchRouter.emailWaveSummaries.query({ limit: 20 })
      setWaves(result.waves)
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load wave report')
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  const loadFailures = async (batchId: string) => {
    try {
      const result = await trpcClient.batchRouter.emailWaveFailures.query({ batchId })
      setFailures(prev => ({ ...prev, [batchId]: result.failures.map(f => f.to) }))
    }
    catch {
      setFailures(prev => ({ ...prev, [batchId]: [] }))
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Delivery report</CardTitle>
            <CardDescription>
              What each send actually achieved. &ldquo;Accepted&rdquo; is what the
              provider took; the rest comes from delivery events, which arrive
              over several minutes — a wave sent just now is mostly Pending, and
              that is normal.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => { load().catch(() => {}) }} disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!error && waves === null && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {!error && waves !== null && waves.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No waves recorded yet. Sends made before the delivery record shipped are not listed.
          </p>
        )}
        {!error && waves !== null && waves.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="py-2 pr-3">Sent</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3 text-right">Accepted</th>
                  <th className="py-2 pr-3 text-right">Delivered</th>
                  <th className="py-2 pr-3 text-right">Bounced</th>
                  <th className="py-2 pr-3 text-right">Spam</th>
                  <th className="py-2 pr-3 text-right">Pending</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {waves.map(wave => (
                  <tr key={wave.batchId} className="border-b align-top">
                    <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                      {new Date(wave.sentAt).toLocaleString()}
                    </td>
                    <td className="max-w-xs truncate py-2 pr-3" title={wave.subject}>
                      {wave.subject}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{wave.accepted}</td>
                    <td className="py-2 pr-3 text-right font-semibold tabular-nums text-green-700">
                      {wave.delivered}
                    </td>
                    <td className={`py-2 pr-3 text-right tabular-nums ${wave.bounced > 0 ? 'text-red-600' : ''}`}>
                      {wave.bounced}
                    </td>
                    <td className={`py-2 pr-3 text-right tabular-nums ${wave.complained > 0 ? 'font-semibold text-red-600' : ''}`}>
                      {wave.complained}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                      {wave.pending}
                    </td>
                    <td className="py-2">
                      {(wave.bounced > 0 || wave.complained > 0) && (
                        failures[wave.batchId]
                          ? (
                              <textarea
                                readOnly
                                rows={3}
                                className="w-64 rounded border p-1 font-mono text-xs"
                                value={failures[wave.batchId].join(', ')}
                              />
                            )
                          : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { loadFailures(wave.batchId).catch(() => {}) }}
                              >
                                Show failures
                              </Button>
                            )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
